import type { FunctionComponent } from 'react'
import React, { useContext as useContextCore } from 'react'
import type { SaveCancelButtonsProps } from '@ztechapps/webui.button'
import { FormSaveCancelButtons } from '@ztechapps/webui.form'
import merge from 'lodash/merge'
import { ProjectAddFormInput } from './ProjectAddFormInput'
import { useProjectAdd } from './useProjectAdd'

export type ProjectAddFormValues = {
  name: string
  summary: string
}

export type ProjectAddComponent = {
  formInput: FunctionComponent
  saveCancelButtons: FunctionComponent<SaveCancelButtonsProps>
}

const defaultComponents: ProjectAddComponent = {
  formInput: ProjectAddFormInput,
  saveCancelButtons: FormSaveCancelButtons,
}

export type ProjectAddProviderProps = {
  onSubmit?: (values: ProjectAddFormValues) => Promise<void>
  onReset?: () => void
  children: React.ReactNode
  initialValues?: ProjectAddFormValues
  components?: ProjectAddComponent
}

export type ProjectAddContext = {
  validate: (value: ProjectAddFormValues) => any
} & Omit<ProjectAddProviderProps, 'children'>

const context = React.createContext<ProjectAddContext>({} as ProjectAddContext)
const CoreProvider = context.Provider

export const ProjectAddProvider = ({
  children,
  components,
  onSubmit,
  initialValues,
  ...props
}: ProjectAddProviderProps) => {
  initialValues = initialValues ?? { name: '[Project Name]', summary: 'A quick summary about this project' }

  const { onSubmit: defaulOnSubmit } = useProjectAdd()

  onSubmit = onSubmit ?? defaulOnSubmit

  //merge the components with the default components
  const mergedComponents = merge(defaultComponents, components)

  const validate = (value: ProjectAddFormValues) => {
    const errors: any = {}
    if (!value?.name) {
      errors.name = 'Required'
    }

    return errors
  }

  return (
    <CoreProvider value={{ initialValues, validate, components: mergedComponents, onSubmit, ...props }}>
      {children}
    </CoreProvider>
  )
}

export const useProjectAddContext = () => useContextCore(context)
